import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { setToken } from '@/utils/tokenManager';
import RegisterForm from './auth/RegisterForm';
import LoginForm from './auth/LoginForm';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'register') {
      setActiveTab('register');
      window.history.replaceState({}, '', '/login');
    }
    if (params.get('registered') === 'true') {
      toast.success('Registration successful! Please log in with your credentials.', { duration: 5000 });
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) { toast.error('Please fill in all fields'); return; }
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: loginData.email, password: loginData.password }),
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Login failed'); }
      const data = await response.json();
      if (data.token) setToken(data.token);
      toast.success('Welcome back!', { duration: 2500 });
      setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
    } catch (error) {
      logger.error('Login exception:', error);
      toast.error(error.message || 'Login failed');
    } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-6 sm:p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Button variant="ghost" data-testid="back-to-home-btn" onClick={() => navigate('/')} className="mb-6 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Home
          </Button>
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_3876ffd2-0b67-4d0f-be27-32eb0667fc3a/artifacts/g551041i_MIF%20Logo%20White.png" alt="MyInvestFolio Logo" className="w-10 h-10 object-contain" />
            <h1 className="text-2xl sm:text-4xl font-serif font-bold text-slate-900">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></h1>
          </div>
          <p className="body-text">
            {activeTab === 'register'
              ? 'Create your account to get started'
              : 'Welcome back! Please sign in to your account'}
          </p>
        </div>
        <Card className="portfolio-card">
          <CardContent className="p-4 sm:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 p-1 rounded-lg h-11">
                <TabsTrigger
                  data-testid="login-tab"
                  value="login"
                  className="font-medium rounded-md transition-all data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger
                  data-testid="register-tab"
                  value="register"
                  className="font-medium rounded-md transition-all data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900"
                >
                  Create Account
                </TabsTrigger>
              </TabsList>
              <TabsContent value="login">
                <LoginForm loginData={loginData} setLoginData={setLoginData} showLoginPassword={showLoginPassword}
                  setShowLoginPassword={setShowLoginPassword} handleLogin={handleLogin} isLoading={isLoading} />
              </TabsContent>
              <TabsContent value="register">
                <RegisterForm isLoading={isLoading} setIsLoading={setIsLoading} navigate={navigate} />
              </TabsContent>
            </Tabs>
            {activeTab === 'login' && (
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><Separator className="w-full" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">Or take a quick look</span></div>
                </div>
                <Button type="button" variant="outline" data-testid="google-auth-btn" onClick={handleGoogleAuth} className="w-full mt-4 portfolio-button-secondary">
                  <Globe className="w-5 h-5 mr-2" />Sign in with Google (Quick View)
                </Button>
                <p className="mt-2 text-xs text-center text-slate-500">
                  Read-only session. To edit, sign in with your email and password.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
